import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlaidLink } from 'react-plaid-link';
import { ROUTES } from '@/constants/routes';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  createPlaidLinkToken,
  exchangePublicToken,
  updatePlaidLinkToken,
} from '@/store/reducers/plaid/actions';
import {
  selectIsLoading,
  selectLinkToken,
  selectIsPlaidLoginRequired,
} from '@/store/reducers/plaid/plaidSlice';

import { selectIsUserFromMobile, selectUser } from '@/store/reducers/user/userSlice';
import analytics from '@/utils/analytics/analyticsInstance';
import { localStorage } from '@/utils/storage';
import { redirectToApp } from '@/utils/appRedirect';
import useSurveyNavigate from '@/hooks/useSurveyNavigate';

export function useConnectPlaid() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { next } = useSurveyNavigate();
  const user = useAppSelector(selectUser);
  const isLoading = useAppSelector(selectIsLoading);
  const linkToken = useAppSelector(selectLinkToken);
  const isFromMobile = useAppSelector(selectIsUserFromMobile);
  const isPlaidLoginRequired = useAppSelector(selectIsPlaidLoginRequired);
  const userId = user?.id;
  const plaidId = user?.plaidIntegrations[0];
  let currentLinkToken;

  if (linkToken) {
    localStorage.save('link_token', linkToken);
    currentLinkToken = linkToken;
  } else {
    currentLinkToken = localStorage.get('link_token');
  }

  const receivedRedirectUri = window.location.href.includes('oauth_state_id')
    ? window.location.href
    : undefined;

  const onConnect = useCallback(async () => {
    try {
      analytics.track('webOnboardingConnectClicked');

      if (isPlaidLoginRequired && plaidId) {
        await dispatch(updatePlaidLinkToken(plaidId)).unwrap();
      } else {
        await dispatch(createPlaidLinkToken()).unwrap();
      }
    } catch (error) {
      analytics.track('plaidConnectionFailed', {
        place: isFromMobile ? 'mobile' : 'web',
        fail_reason: (error as Error).message,
      });

      navigate(ROUTES.PLAID_FAILED);
    }
  }, [dispatch, navigate, isFromMobile, isPlaidLoginRequired, plaidId]);

  const onSuccess = useCallback(
    async (publicToken: string) => {
      try {
        if (!userId) {
          return;
        }
        await dispatch(exchangePublicToken({ publicToken, userId })).unwrap();

        await analytics.forceTrack('plaidConnectionSucceed', {
          place: isFromMobile ? 'mobile' : 'web',
        });

        localStorage.remove('link_token');

        if (isFromMobile || isPlaidLoginRequired) {
          const accessToken = localStorage.get('accessToken');

          redirectToApp(accessToken);
        } else {
          next();
        }
      } catch (error) {
        analytics.track('plaidConnectionFailed', {
          place: isFromMobile ? 'mobile' : 'web',
          fail_reason: (error as Error).message,
        });

        navigate(ROUTES.PLAID_FAILED);
      }
    },
    [dispatch, navigate, next, userId, isFromMobile, isPlaidLoginRequired],
  );

  const config: Parameters<typeof usePlaidLink>[0] = {
    token: currentLinkToken,
    onSuccess,
    receivedRedirectUri,
  };

  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (ready) {
      analytics.track('plaidConnectionStarted', {
        place: isFromMobile ? 'mobile' : 'web',
      });

      open();
    }
  }, [ready, open, isFromMobile]);

  return { isLoading, onConnect, ready };
}
